import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { Product, ProductQuota } from '../lib/types'
import type { ProductPage, ProductView } from '../lib/productView'
import type { useProductStatus } from '../hooks/useProductStatus'
import { QueryStateCard } from './ui/QueryStateCard'
import { NotContractedPreview } from './NotContractedPreview'

/** A product tab's body, chosen by the status matrix (MS-819): the one place
 * the five tabbed pages turn `useProductStatus` into what to render.
 *
 * - status still loading: the loading card;
 * - status failed: "We couldn't load your subscription status." with Retry
 *   (Q7, decided by the PO 2026-10-07: option A — the title and tabs stay,
 *   no view is guessed);
 * - view `preview`: the product's preview card (Not Contracted for both
 *   products, Q2/Q13; Expired on Additional Volume / User Limits; anything
 *   but Contracted on Excess Volume);
 * - otherwise `children(view, current)`: the page's own content for `full`,
 *   `trial` or `expired`.
 *
 * Data already on screen wins over a failed background refetch: an error is
 * only shown while there is no row to render. */
export function ProductViewGate({
  status,
  page,
  product,
  children,
}: Readonly<{
  status: ReturnType<typeof useProductStatus>
  page: ProductPage
  product: Product
  children: (view: Exclude<ProductView, 'preview'>, current: ProductQuota) => ReactNode
}>) {
  const { t } = useTranslation()
  const view = status.view(page, product)
  const current = status.quota?.find((q) => q.product === product)

  if (view === undefined || current === undefined) {
    // Loaded but without this product's row is a contract breach of /quota
    // (it always answers for every product) — shown as the error, never
    // guessed into a view.
    if (status.isError || status.quota !== undefined) {
      return (
        <QueryStateCard
          state="error"
          message={t('common.statusLoadError')}
          onRetry={() => status.refetch()}
        />
      )
    }
    return <QueryStateCard state="pending" />
  }
  if (view === 'preview') return <NotContractedPreview product={product} page={page} />
  return <>{children(view, current)}</>
}
